import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Tab, Tabs, TextField, Typography, useTheme,
} from '@mui/material';
import {
  CheckCircleOutline as DoneIcon, Close as CloseIcon, FileDownloadOutlined as DownloadIcon,
  SendOutlined as SendIcon,
} from '@mui/icons-material';
import { pdf } from '@react-pdf/renderer';
import { ExtendedTicket } from '../../types';
import {
  NIS2_STAGES, Nis2EntityProfile, Nis2Report, Nis2Significance, Nis2Stage, Nis2StageDataMap,
} from '../../types/nis2';
import { Nis2Service } from '../../services/nis2Service';
import {
  STAGE_META, buildEntityDefaults, buildSignificanceDefaults, buildStageDefaults, draftFor, finalReportTitle,
  formatDateTime, formatRemaining, fromLocalInput, latestSubmitted, stageReports, toLocalInput,
} from './nis2Helpers';
import {
  AssessmentForm, Banner, EarlyWarningForm, FinalForm, IntermediateForm, NotificationForm, useInputSx,
} from './Nis2Forms';
import Nis2ReportPdf from './Nis2ReportPdf';

type TabKey = 'assessment' | Nis2Stage;

interface Props {
  open: boolean;
  onClose: () => void;
  ticket: ExtendedTicket;
}

const buildAllStageForms = (ticket: ExtendedTicket, report: Nis2Report): Nis2StageDataMap => ({
  early_warning: buildStageDefaults('early_warning', ticket, report),
  notification: buildStageDefaults('notification', ticket, report),
  intermediate: buildStageDefaults('intermediate', ticket, report),
  final: buildStageDefaults('final', ticket, report),
});

const snapshot = (v: unknown) => JSON.stringify(v);

const initialTab = (report: Nis2Report): TabKey => {
  if (!report.awareness_at) return 'assessment';
  const next = (['early_warning', 'notification', 'final'] as Nis2Stage[]).find(s => !report.deadlines[s]?.completed);
  return next || 'final';
};

const Nis2ReportModal: React.FC<Props> = ({ open, onClose, ticket }) => {
  const theme = useTheme();
  const inputSx = useInputSx();

  const [report, setReport] = useState<Nis2Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<TabKey>('assessment');
  const [now, setNow] = useState(Date.now());

  const [awarenessAt, setAwarenessAt] = useState('');
  const [entity, setEntity] = useState<Nis2EntityProfile>(buildEntityDefaults(null));
  const [significance, setSignificance] = useState<Nis2Significance>(buildSignificanceDefaults(null));
  const [forms, setForms] = useState<Nis2StageDataMap | null>(null);
  const [baseline, setBaseline] = useState<Record<TabKey, string>>({} as Record<TabKey, string>);
  const [submitAt, setSubmitAt] = useState<string | null>(null); // non-null = confirm panel open

  const assessmentState = useMemo(() => ({ awarenessAt, entity, significance }), [awarenessAt, entity, significance]);

  const isDirty = useCallback((key: TabKey) => {
    if (key === 'assessment') return baseline.assessment !== snapshot(assessmentState);
    return !!forms && baseline[key] !== snapshot(forms[key]);
  }, [baseline, assessmentState, forms]);

  const anyDirty = (['assessment', ...NIS2_STAGES] as TabKey[]).some(isDirty);

  const applyReport = useCallback((next: Nis2Report, resetStages: Nis2Stage[] | 'all') => {
    setReport(next);
    const nextAssessment = {
      awarenessAt: next.awareness_at || '',
      entity: buildEntityDefaults(next),
      significance: buildSignificanceDefaults(next),
    };
    setAwarenessAt(nextAssessment.awarenessAt);
    setEntity(nextAssessment.entity);
    setSignificance(nextAssessment.significance);

    const rebuilt = buildAllStageForms(ticket, next);
    const stages = resetStages === 'all' ? NIS2_STAGES : resetStages;
    setForms(prev => {
      const merged = { ...(prev || rebuilt) } as Nis2StageDataMap;
      stages.forEach(s => { (merged as any)[s] = rebuilt[s]; });
      return merged;
    });
    setBaseline(prev => {
      const b = { ...prev, assessment: snapshot(nextAssessment) };
      stages.forEach(s => { b[s] = snapshot(rebuilt[s]); });
      return b;
    });
  }, [ticket]);

  // Load on open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setFeedback(null);
    setSubmitAt(null);
    Nis2Service.getReport(ticket.id)
      .then(r => {
        if (cancelled) return;
        applyReport(r, 'all');
        setTab(initialTab(r));
      })
      .catch(e => !cancelled && setLoadError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, ticket.id, applyReport]);

  // Countdown ticker
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [open]);

  const handleClose = () => {
    if (anyDirty && !window.confirm('You have unsaved NIS2 report changes. Close anyway?')) return;
    onClose();
  };

  // Stages that nobody has touched and have no server draft are re-derived after
  // each save, so e.g. submitting the early warning pre-fills the 72h notification.
  const untouchedStages = (next: Nis2Report, except?: Nis2Stage) =>
    NIS2_STAGES.filter(s => s !== except && !isDirty(s) && !draftFor(next, s));

  const saveAssessment = async (): Promise<Nis2Report> => {
    const next = await Nis2Service.updateAssessment(ticket.id, {
      awareness_at: awarenessAt || null,
      assessment: { entity, significance },
    });
    return next;
  };

  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    setFeedback(null);
    try {
      setFeedback({ severity: 'success', message: await fn() });
    } catch (e: any) {
      setFeedback({ severity: 'error', message: e.message });
    } finally {
      setBusy(false);
    }
  };

  const handleSaveAssessment = () => run(async () => {
    const next = await saveAssessment();
    applyReport(next, untouchedStages(next));
    return 'Assessment saved.';
  });

  const handleSaveStage = (stage: Nis2Stage, submittedAtIso?: string) => run(async () => {
    if (!forms) throw new Error('Report not loaded');
    if (isDirty('assessment')) await saveAssessment();
    const status = submittedAtIso ? 'submitted' : 'draft';
    const next = await Nis2Service.saveStage(ticket.id, stage, forms[stage], status, submittedAtIso);
    // Draft save re-reads the same data from the server; submit rolls the form over to the submitted copy.
    applyReport(next, [stage, ...untouchedStages(next, stage)]);
    setSubmitAt(null);
    return submittedAtIso
      ? `${stageTitle(stage)} marked as submitted on ${formatDateTime(submittedAtIso)}.`
      : 'Draft saved.';
  });

  const stageTitle = (stage: Nis2Stage) =>
    stage === 'final' && forms ? finalReportTitle(forms.final) : STAGE_META[stage].title;

  const handleDownload = (stage: Nis2Stage) => run(async () => {
    if (!forms) throw new Error('Report not loaded');
    const generatedAt = new Date().toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
    });
    const blob = await pdf(
      <Nis2ReportPdf
        stage={stage}
        data={forms[stage]}
        entity={entity}
        significance={significance}
        ticketId={ticket.id}
        awarenessAt={awarenessAt || null}
        dueAt={report?.deadlines[stage]?.due_at || null}
        generatedAt={generatedAt}
      />,
    ).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nis2-${stage.replace('_', '-')}-${ticket.id.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
    return 'PDF downloaded.';
  });

  const patchStage = <S extends Nis2Stage>(stage: S) => (patch: Partial<Nis2StageDataMap[S]>) =>
    setForms(prev => (prev ? { ...prev, [stage]: { ...prev[stage], ...patch } } : prev));

  // ─── Deadline strip ────────────────────────────────────────────────────────

  const stageStatus = (stage: Nis2Stage): { text: string; color: string; done: boolean } => {
    const d = report?.deadlines[stage];
    const last = latestSubmitted(report, stage);
    if (d?.completed && last) {
      return { text: `Submitted ${formatDateTime(last.submitted_at)}`, color: theme.palette.success.main, done: true };
    }
    if (stage === 'final' && last) {
      const due = d?.due_at ? ` · final ${formatRemaining(d.due_at, now).text}` : '';
      return { text: `Progress report submitted${due}`, color: theme.palette.warning.main, done: false };
    }
    if (stage === 'intermediate') {
      return { text: draftFor(report, stage) ? 'Draft · on request' : 'On request only', color: theme.palette.text.tertiary, done: false };
    }
    if (!d?.due_at) {
      const reason = stage === 'final' ? 'After 72h notification' : 'Set T0 to start clock';
      return { text: reason, color: theme.palette.text.tertiary, done: false };
    }
    const r = formatRemaining(d.due_at, now);
    const color = r.overdue ? theme.palette.error.main : r.urgent ? theme.palette.warning.main : theme.palette.text.secondary;
    return { text: `${draftFor(report, stage) ? 'Draft · ' : ''}${r.text}`, color, done: false };
  };

  const tabLabel = (key: TabKey) => {
    const text = key === 'assessment' ? 'Assessment'
      : key === 'notification' && entity.trust_service_provider ? 'Notification · 24h'
        : STAGE_META[key].tab;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {key !== 'assessment' && stageStatus(key).done && <DoneIcon sx={{ fontSize: 14, color: theme.palette.success.main }} />}
        {text}
        {isDirty(key) && <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.warning.main }} />}
      </Box>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  const renderStage = (stage: Nis2Stage) => {
    if (!forms) return null;
    const meta = STAGE_META[stage];
    const submitted = stageReports(report, stage).filter(n => n.status === 'submitted');
    const needsSignificance = significance.decision !== 'significant' && stage !== 'intermediate';
    return (
      <>
        <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary, mb: 1.5 }}>
          <strong>{meta.article}.</strong> {meta.purpose}
          {report?.deadlines[stage]?.due_at && (
            <> Deadline: <strong>{formatDateTime(report.deadlines[stage].due_at)}</strong> ({report.deadlines[stage].basis}).</>
          )}
        </Typography>
        {needsSignificance && (
          <Banner severity="info" sx={{ mb: 2, fontSize: '0.78rem' }}>
            Mandatory reporting applies to significant incidents. The current assessment is
            {' '}<strong>{significance.decision.replace('_', ' ')}</strong>. Reports on non-significant incidents
            are voluntary (Art. 30).
          </Banner>
        )}
        {submitted.length > 0 && (
          <Banner severity="success" icon={<DoneIcon fontSize="small" />} sx={{ mb: 2, fontSize: '0.78rem' }}>
            {submitted.map(n => (
              <div key={n.id}>
                {stage === 'final' ? finalReportTitle(n.data) : meta.title} submitted {formatDateTime(n.submitted_at)}
                {n.created_by ? ` by ${n.created_by.full_name || n.created_by.username || n.created_by.email}` : ''}
              </div>
            ))}
            {stage !== 'early_warning' && stage !== 'notification'
              ? 'Editing below starts a new report for this stage.'
              : 'Editing below starts a corrected resubmission.'}
          </Banner>
        )}
        {stage === 'early_warning' && <EarlyWarningForm value={forms.early_warning} onChange={patchStage('early_warning')} />}
        {stage === 'notification' && <NotificationForm value={forms.notification} onChange={patchStage('notification')} />}
        {stage === 'intermediate' && <IntermediateForm value={forms.intermediate} onChange={patchStage('intermediate')} />}
        {stage === 'final' && <FinalForm value={forms.final} onChange={patchStage('final')} />}
      </>
    );
  };

  const stageTab = tab !== 'assessment' ? tab : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
          border: `1px solid ${theme.palette.border?.main ?? 'rgba(255,255,255,0.12)'}`,
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 0, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: theme.palette.text.primary }}>
              NIS2 Incident Report
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
              Directive (EU) 2022/2555, Art. 23 &nbsp;·&nbsp; Implementing Regulation (EU) 2024/2690
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} sx={{ color: theme.palette.text.tertiary, mt: 0.25 }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {report && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1, mt: 1.5 }}>
            {NIS2_STAGES.map(s => {
              const st = stageStatus(s);
              return (
                <Box key={s} onClick={() => setTab(s)} sx={{
                  cursor: 'pointer', p: 1, borderRadius: 1,
                  border: `1px solid ${tab === s ? theme.palette.primary.main : theme.palette.divider}`,
                  backgroundColor: theme.palette.background.tertiary,
                }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700 }}>{STAGE_META[s].title}</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: st.color }}>{st.text}</Typography>
                </Box>
              );
            })}
          </Box>
        )}

        <Tabs value={tab} onChange={(_, v) => { setTab(v); setSubmitAt(null); }} variant="scrollable"
          sx={{ mt: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontSize: '0.8rem' } }}>
          {(['assessment', ...NIS2_STAGES] as TabKey[]).map(k => <Tab key={k} value={k} label={tabLabel(k)} />)}
        </Tabs>
      </DialogTitle>

      <DialogContent sx={{ pt: '20px !important', pb: 1 }}>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>}
        {loadError && <Banner severity="error">{loadError}</Banner>}
        {!loading && !loadError && report && (
          tab === 'assessment' ? (
            <AssessmentForm
              awarenessAt={awarenessAt}
              onAwarenessChange={setAwarenessAt}
              suggestedAwareness={ticket.alert?.detection_timestamp}
              entity={entity}
              onEntityChange={p => setEntity(prev => ({ ...prev, ...p }))}
              significance={significance}
              onSignificanceChange={p => setSignificance(prev => ({ ...prev, ...p }))}
            />
          ) : renderStage(tab)
        )}
      </DialogContent>

      {feedback && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Banner severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ fontSize: '0.8rem' }}>
            {feedback.message}
          </Banner>
        </Box>
      )}

      {stageTab && submitAt !== null && (
        <Box sx={{
          px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
          borderTop: `1px solid ${theme.palette.divider}`,
        }}>
          <Typography sx={{ fontSize: '0.8rem', flex: 1, minWidth: 200 }}>
            Record that the {stageTitle(stageTab).toLowerCase()} was transmitted to the authority at:
          </Typography>
          <TextField size="small" type="datetime-local" value={submitAt} onChange={e => setSubmitAt(e.target.value)}
            sx={{ ...inputSx, width: 220 }} />
          <Button size="small" onClick={() => setSubmitAt(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button size="small" variant="contained" disabled={busy || !fromLocalInput(submitAt)}
            onClick={() => handleSaveStage(stageTab, fromLocalInput(submitAt)!)} sx={{ textTransform: 'none' }}>
            Confirm submission
          </Button>
        </Box>
      )}

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}`, gap: 1 }}>
        <Button onClick={handleClose} sx={{ color: theme.palette.text.secondary, textTransform: 'none', fontSize: '0.85rem', mr: 'auto' }}>
          Close
        </Button>
        {tab === 'assessment' && (
          <Button variant="contained" disabled={busy || !report} onClick={handleSaveAssessment}
            sx={{ textTransform: 'none', fontSize: '0.85rem', fontWeight: 600 }}>
            Save assessment
          </Button>
        )}
        {stageTab && (
          <>
            <Button variant="outlined" disabled={busy || !forms} onClick={() => handleDownload(stageTab)}
              startIcon={<DownloadIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none', fontSize: '0.85rem' }}>
              Download PDF
            </Button>
            <Button variant="outlined" disabled={busy || !forms} onClick={() => handleSaveStage(stageTab)}
              sx={{ textTransform: 'none', fontSize: '0.85rem' }}>
              Save draft
            </Button>
            <Button variant="contained" disabled={busy || !forms || submitAt !== null}
              onClick={() => setSubmitAt(toLocalInput(new Date().toISOString()))}
              startIcon={<SendIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontSize: '0.85rem', fontWeight: 600 }}>
              Mark as submitted
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default Nis2ReportModal;
